import type { ReactNode } from 'react';
import { Card, Divider, Flex, Tag, Typography } from 'antd';
import {
  IconBuilding,
  IconCalendar,
  IconClock,
  IconCoinEuro,
  IconPercentage,
  IconShieldCheck,
} from '@tabler/icons-react';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { SlaStatusTag } from '@/app/landing/sla/shared/components/SlaStatusTag';
import { SLA_OEM_RATE_MATRIX_SCROLL_Y } from '@/app/landing/sla/shared/sla.constants';
import {
  formatCurrency,
  formatDateLabel,
} from '@/app/landing/sla/shared/utils/sla-format.utils';

const { Text, Title } = Typography;

type ReviewRowProps = {
  icon: ReactNode;
  label: string;
  value: React.ReactNode;
  accent?: string;
};

function ReviewRow({ icon, label, value, accent = '#1e6df6' }: ReviewRowProps) {
  return (
    <Flex align="center" justify="space-between" gap={12}>
      <Flex align="center" gap={10}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: `${accent}12`,
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
        <Text type="secondary">{label}</Text>
      </Flex>
      <Text strong>{value}</Text>
    </Flex>
  );
}

type LandlordReviewProps = {
  partnerName: string;
  uptimeTargetPct?: number | null;
  responseDcDays?: number;
  responseAcDays?: number;
  penaltyPerDayAmount?: number;
  penaltyCurrency?: string;
  paymentModel?: string;
  status?: string;
  validFrom?: Dayjs | null;
  validTo?: Dayjs | null;
};

export function LandlordSlaReviewSummary({
  partnerName,
  uptimeTargetPct,
  responseDcDays,
  responseAcDays,
  penaltyPerDayAmount,
  penaltyCurrency,
  paymentModel,
  status,
  validFrom,
  validTo,
}: LandlordReviewProps) {
  const { t } = useTranslation('sla');

  return (
    <Card
      style={{
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 28%)',
      }}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
        <Flex align="center" gap={12}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(30, 109, 246, 0.1)',
              color: '#1e6df6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconBuilding size={22} />
          </div>
          <div>
            <Text type="secondary" className="ds-small-label">
              {t('review.landlordPackage')}
            </Text>
            <Title level={4} style={{ margin: 0 }}>
              {partnerName}
            </Title>
          </div>
          {status ? <SlaStatusTag status={status} /> : null}
        </Flex>
      </div>

      <div style={{ padding: '20px 24px' }}>
        <Flex vertical gap={14}>
          <ReviewRow
            icon={<IconPercentage size={18} />}
            label={t('landlords.fields.uptime')}
            value={uptimeTargetPct != null ? `${uptimeTargetPct}%` : '—'}
          />
          <ReviewRow
            icon={<IconClock size={18} />}
            label={t('landlords.columns.response')}
            value={`${responseDcDays ?? '—'}d DC / ${responseAcDays ?? '—'}d AC`}
            accent="#00a87e"
          />
          <ReviewRow
            icon={<IconCoinEuro size={18} />}
            label={t('landlords.fields.penalty')}
            value={formatCurrency(penaltyPerDayAmount, penaltyCurrency)}
            accent="#e23b4a"
          />
          <ReviewRow
            icon={<IconShieldCheck size={18} />}
            label={t('landlords.fields.paymentModel')}
            value={
              paymentModel
                ? t(`paymentModel.${paymentModel}`, paymentModel)
                : '—'
            }
            accent="#7c3aed"
          />
        </Flex>

        <Divider style={{ margin: '18px 0' }} />

        <Flex vertical gap={10}>
          <Text className="ds-section-header">{t('review.validity')}</Text>
          <Flex gap={8} wrap>
            <Tag
              icon={<IconCalendar size={14} />}
              color="processing"
              style={{ borderRadius: 9999, paddingInline: 12 }}
            >
              {t('review.from', { date: formatDateLabel(validFrom) })}
            </Tag>
            {validTo ? (
              <Tag
                icon={<IconCalendar size={14} />}
                style={{ borderRadius: 9999, paddingInline: 12 }}
              >
                {t('review.to', { date: formatDateLabel(validTo) })}
              </Tag>
            ) : (
              <Tag style={{ borderRadius: 9999, paddingInline: 12 }}>
                {t('review.openEnded')}
              </Tag>
            )}
          </Flex>
        </Flex>
      </div>
    </Card>
  );
}

type OemRateReview = {
  windowValue?: number;
  windowUnit?: string;
  costAmount?: number;
  currency?: string;
};

type OemReviewProps = {
  partnerName: string;
  status?: string;
  validFrom?: Dayjs | null;
  validTo?: Dayjs | null;
  rateEntries?: OemRateReview[];
};

export function OemSlaReviewSummary({
  partnerName,
  status,
  validFrom,
  validTo,
  rateEntries = [],
}: OemReviewProps) {
  const { t } = useTranslation('sla');

  return (
    <Card
      style={{
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        background: 'linear-gradient(180deg, #faf8ff 0%, #ffffff 28%)',
      }}
      styles={{ body: { padding: 0 } }}
    >
      <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
        <Flex align="center" gap={12}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: 'rgba(124, 58, 237, 0.1)',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconShieldCheck size={22} />
          </div>
          <div>
            <Text type="secondary" className="ds-small-label">
              {t('review.oemPackage')}
            </Text>
            <Title level={4} style={{ margin: 0 }}>
              {partnerName}
            </Title>
          </div>
          {status ? <SlaStatusTag status={status} /> : null}
        </Flex>
      </div>

      <div style={{ padding: '20px 24px' }}>
        <Flex gap={8} wrap style={{ marginBottom: 16 }}>
          <Tag
            icon={<IconCalendar size={14} />}
            color="processing"
            style={{ borderRadius: 9999, paddingInline: 12 }}
          >
            {t('review.from', { date: formatDateLabel(validFrom) })}
          </Tag>
          {validTo ? (
            <Tag
              icon={<IconCalendar size={14} />}
              style={{ borderRadius: 9999, paddingInline: 12 }}
            >
              {t('review.to', { date: formatDateLabel(validTo) })}
            </Tag>
          ) : (
            <Tag style={{ borderRadius: 9999, paddingInline: 12 }}>
              {t('review.openEnded')}
            </Tag>
          )}
        </Flex>

        <Text className="ds-section-header">{t('review.rateOptions')}</Text>
        <div
          className="sla-oem-rate-review-box"
          style={{ maxHeight: SLA_OEM_RATE_MATRIX_SCROLL_Y }}
        >
          <Flex vertical gap={10}>
            {rateEntries.map((entry, index) => (
              <Card
                key={`${entry.windowValue}-${entry.windowUnit}-${index}`}
                size="small"
                style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
              >
                <Flex justify="space-between" align="center">
                  <Flex align="center" gap={8}>
                    <IconClock size={16} color="#7c3aed" />
                    <Text>
                      {entry.windowValue} {entry.windowUnit}
                    </Text>
                  </Flex>
                  <Text strong style={{ color: '#00a87e' }}>
                    {formatCurrency(entry.costAmount, entry.currency)}
                  </Text>
                </Flex>
              </Card>
            ))}
          </Flex>
        </div>
      </div>
    </Card>
  );
}
